/**
 * The chat store — orchestrates ONE companion's conversation: history load, the
 * detached-run SSE lifecycle, and the generative-UI form round-trip. Holds no AI
 * logic; it drives the {@link ApiClient} and folds the run's events through the
 * pure {@link applyChatEvent} reducer into a signal the view renders.
 *
 * Detached run: `send` opens the SSE VIEW of a server-side run and tails it. A
 * `waiting_input` park is NOT terminal, so the same open stream keeps tailing
 * across a `submit` — the resumed run's tokens arrive on the same channel. On a
 * clean finish, history is re-fetched (the edge is the authority) and the live
 * stream is cleared into it.
 *
 * The active companion→conversation mapping is client state (there is no global
 * "current GF"): it is remembered in `localStorage` so reopening a companion
 * lands on her existing thread.
 */
import { type ApiError, isErr } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import { toApiError } from '../api/envelope.ts';
import { NetworkError } from '../api/errors.ts';
import type { ConversationView, MessageView } from '../api/types.ts';
import {
  initialStreamState,
  markDirectiveAnswered,
  type StreamState,
} from '../features/chat/stream-model.ts';
import type { ChatStore, ConversationMemory, OpenCompanionOptions } from './chat-contract.ts';
import { type ChatScheduler, replayOutbox } from './chat-delivery.ts';
import { createMediaReconciler } from './chat-media.ts';
import { createRunView } from './chat-view.ts';
import { createOutbox, type Outbox, type OutboxEntry } from './outbox.ts';

// The contract, the delivery rules, the run view and media resolution live next door
// (this file is at its LOC ceiling), but they are all part of the store's surface —
// callers keep importing them from here.
export type * from './chat-contract.ts';
export { OUTBOX_DRAIN_TIMEOUT_MS } from './chat-delivery.ts';
export { MEDIA_POLL_INTERVAL_MS } from './chat-media.ts';
export type { ChatScheduler };

const defaultScheduler: ChatScheduler = (fn, ms) => {
  const handle = setTimeout(fn, ms);
  return () => clearTimeout(handle);
};

/** The default `localStorage`-backed memory; a no-op when storage is unavailable (SSR/tests). */
export function browserConversationMemory(): ConversationMemory {
  const key = (companionId: string): string => `4f:conversation:${companionId}`;
  return {
    get(companionId) {
      try {
        return globalThis.localStorage?.getItem(key(companionId)) ?? null;
      } catch {
        return null;
      }
    },
    set(companionId, conversationId) {
      try {
        globalThis.localStorage?.setItem(key(companionId), conversationId);
      } catch {
        // Storage disabled (private mode) — the thread just won't persist across reloads.
      }
    },
  };
}

export function createChatStore(
  api: ApiClient,
  memory: ConversationMemory = browserConversationMemory(),
  schedule: ChatScheduler = defaultScheduler,
  outbox: Outbox = createOutbox(),
): ChatStore {
  const [conversation, setConversation] = createSignal<ConversationView | null>(null);
  const [history, setHistory] = createSignal<readonly MessageView[]>([]);
  const [historyTruncated, setHistoryTruncated] = createSignal(false);
  const [stream, setStream] = createSignal<StreamState>(initialStreamState);
  const [pendingUserText, setPendingUserText] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [uploading, setUploading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);
  // Mirror of the durable outbox, so the view re-renders when the queue changes.
  const [queued, setQueued] = createSignal<readonly OutboxEntry[]>([]);

  // The open companion's conversation id, tracked separately from the loaded
  // `conversation()` VIEW: the id is resolved (or created) first, and the history
  // read after it can fail on its own — offline, or a `5xx`. Keying sends off the
  // view meant a reopened thread whose history did not load could not be typed
  // into at all: `send` no-op'd, and the composer cleared the text anyway. A
  // SIGNAL, not a plain field, because the realtime handlers route pushes by it.
  const [conversationId, setConversationId] = createSignal<string | null>(null);

  let controller: AbortController | null = null;
  let companionId: string | null = null;
  // Monotonic history-read token. Every reader (open, settle, refresh, replay)
  // races the others, and `getConversation` resolves out of order — so the LAST
  // read to return used to win, not the last one issued, and a slow reload could
  // paint a pre-turn snapshot over a turn that had just completed.
  let historyRead = 0;
  // Presentation-only: her media reveals behind the faked "generating…" beat.
  let prebuilt = false;
  // The last turn the user sent — replayed by `retry` after a stream error.
  let lastSentText: string | null = null;
  // Guards `flushOutbox` against overlapping itself (the `online` event fires on every
  // flap of a bad connection, and a replay now spans whole runs).
  let flushing = false;

  // Media resolution (WS swap + poll fallback) lives next door; it reads the same
  // stream + companion this store owns through accessors, so a companion switch
  // invalidates its in-flight work exactly as it does the store's own.
  const media = createMediaReconciler({
    api,
    schedule,
    stream,
    setStream,
    companionId: () => companionId,
    prebuilt: () => prebuilt,
  });
  const schedulePoll = (): void => media.schedulePoll();
  const stopPolling = (): void => media.stopPolling();

  // The run's SSE view — folding the channel into `stream`, and surviving a channel
  // that dies under it by re-attaching to the same (detached, still billing) run.
  const runView = createRunView({
    attach: (cid, runId, signal) => api.attach(cid, runId, signal),
    setStream,
    resetStream: () => setStream(initialStreamState),
    currentRunId: () => stream().runId,
    setError: (failure) => setError(failure),
  });

  const cancelStream = (): void => {
    controller?.abort();
    controller = null;
  };

  /** Re-read the durable queue for whichever conversation is open. */
  const syncQueued = (): void => {
    const convId = conversationId();
    setQueued(convId ? outbox.listFor(convId) : []);
  };

  /** Resolve (or lazily create + remember) the conversation id for a companion. */
  const ensureConversation = async (gfId: string): Promise<string | null> => {
    const known = memory.get(gfId);
    if (known) {
      return known;
    }
    const created = await api.createConversation(gfId);
    if (isErr(created)) {
      setError(created.error);
      return null;
    }
    memory.set(gfId, created.value.id);
    return created.value.id;
  };

  /**
   * Re-read one conversation's history and commit it — unless a NEWER read (or a
   * companion switch) has since been issued. The edge is authority, but "the edge
   * is authority" only holds for its latest answer: four call sites reload this
   * (open, a settled turn, the WS refresh, an outbox replay) and they overlap by
   * design, so without the token a slow earlier read lands last and paints a
   * snapshot from before the turn that just completed.
   */
  const loadHistory = async (convId: string): Promise<void> => {
    historyRead += 1;
    const read = historyRead;
    const detail = await api.getConversation(convId);
    if (isErr(detail)) {
      setError(detail.error);
      return;
    }
    if (read !== historyRead || conversationId() !== convId) {
      return;
    }
    setConversation(detail.value.conversation);
    setHistory(detail.value.messages);
    // Absent ⇒ an edge that does not cap this read, i.e. nothing was trimmed.
    setHistoryTruncated(detail.value.hasMore ?? false);
    syncQueued();
  };

  const openCompanion = async (gfId: string, options?: OpenCompanionOptions): Promise<void> => {
    cancelStream();
    stopPolling();
    companionId = gfId;
    setConversationId(null);
    prebuilt = options?.prebuilt ?? false;
    lastSentText = null;
    setError(null);
    setStream(initialStreamState);
    setPendingUserText(null);
    // Drop the previous companion's thread NOW rather than on the next successful
    // read: a load that fails (offline, `5xx`) used to leave her messages, her
    // queued turns and her truncation notice rendered under the new companion's
    // name and avatar.
    setConversation(null);
    setHistory([]);
    setQueued([]);
    setHistoryTruncated(false);
    setBusy(true);
    try {
      const resolved = await ensureConversation(gfId);
      if (resolved && companionId === gfId) {
        setConversationId(resolved);
        // Show what is still queued for HER thread even if the history read below
        // fails — those turns are pending, and hiding them is exactly the silence
        // the outbox exists to remove.
        syncQueued();
        await loadHistory(resolved);
      }
    } finally {
      if (companionId === gfId) {
        setBusy(false);
      }
    }
  };

  const send = async (text: string): Promise<boolean> => {
    const convId = conversationId();
    const gfId = companionId;
    if (!convId || busy()) {
      // NOT taken: there is no thread to send to yet, or a turn is already in
      // flight. The caller still holds the text — see `ChatStore.send`.
      return false;
    }
    cancelStream();
    const local = new AbortController();
    controller = local;
    setError(null);
    setStream(initialStreamState);
    setPendingUserText(text);
    lastSentText = text;
    setBusy(true);
    try {
      // The POST is deliberately NOT abortable, and `local.signal` closes only the
      // READER below. Aborting the request makes an accepted turn indistinguishable
      // from an undelivered one — both surface as `network_error` — so whichever
      // way that ambiguity is resolved costs the user something: queue it and an
      // accepted turn is re-sent and re-billed; drop it and a turn that never left
      // the device is gone. Left un-aborted, the answer is unambiguous, and the run
      // is server-side and detached anyway: aborting never stopped it.
      const opened = await api.sendMessage(convId, text);
      if (isErr(opened)) {
        // Offline: QUEUE the turn instead of losing it. Only a transport failure
        // is queued — a domain rejection (over quota, moderation) is a real "no"
        // and must not be silently retried later.
        if (opened.error.code === 'network_error') {
          outbox.enqueue(convId, text);
          syncQueued();
          if (controller === local) {
            setPendingUserText(null);
          }
        }
        if (controller === local) {
          setError(opened.error);
        }
        return true;
      }
      if (local.signal.aborted) {
        // The view was superseded (companion switch, `dispose()`) while the edge
        // answered. Release the channel — the run carries on server-side — and
        // touch none of the state, which now belongs to another companion.
        void opened.value.body?.cancel().catch(() => undefined);
        return true;
      }
      await runView.tail(opened.value, local.signal, convId);
      // A clean finish OR a stop → re-sync from the authoritative history and clear
      // the view. Cancel belongs here too: the user turn IS persisted while the
      // partial reply is NOT, so leaving the optimistic bubble + never-stored text
      // on screen shows a conversation the server does not have — and the next WS
      // `refresh()` would then render the user's turn twice.
      const settled = stream().status;
      if (
        (settled === 'finished' || settled === 'canceled') &&
        companionId === gfId &&
        !local.signal.aborted
      ) {
        await loadHistory(convId);
        setStream(initialStreamState);
        setPendingUserText(null);
      }
      return true;
    } catch (cause) {
      // Nothing above should reach here — `tailRun` owns the channel's failures —
      // but every caller fires this as `void store.send(…)`, so an unexpected throw
      // would become an unhandled rejection with the turn left mid-air and no
      // outcome on screen. Failing the turn loudly beats that.
      if (!local.signal.aborted && controller === local) {
        runView.fail(toApiError(new NetworkError(cause)));
      }
      return true;
    } finally {
      if (controller === local) {
        setBusy(false);
      }
      // The run's channel is done, but a media job it started outlives it. Arm the
      // poll so the placeholder resolves even if the WS push never arrives.
      schedulePoll();
    }
  };

  /**
   * Ask the edge to stop the live run. Deliberately does NOT abort the local
   * reader: the run's `run-canceled` event is what closes the stream, and that
   * event is also what tells the user their stop actually landed. Aborting here
   * would hide the outcome and leave the "did it stop?" question unanswered.
   *
   * A rejection (the run already ended → `404`) is swallowed rather than shown:
   * "stop" against something that just finished is not an error the user can act on.
   */
  const stop = async (): Promise<void> => {
    const convId = conversationId();
    const runId = stream().runId;
    if (!convId || !runId) {
      return;
    }
    await api.cancelRun(convId, runId);
  };

  /**
   * Replay the durable queue — see {@link replayOutbox} for the rules, and
   * {@link ChatStore.flushOutbox} for why it is not scoped to the open thread.
   *
   * Deliberately NOT gated on `busy()`: a flush fired while a run streams would
   * simply do nothing, and the trigger (`online`, boot) does not come round again —
   * the queue would wait for the next network flap. The per-conversation blocking
   * inside the replay already keeps a busy thread's own turns in order, so the
   * live run costs nothing but a `409` on the thread it owns.
   */
  const flushOutbox = async (): Promise<void> => {
    // Re-entrant by call site: `online` fires on every flap of a bad connection, and
    // each replay spans whole runs, so two flushes would otherwise overlap for that
    // long and re-POST the same entry (the `remove` lands after an await).
    if (flushing) {
      return;
    }
    flushing = true;
    try {
      const delivered = await replayOutbox({
        // No abort signal: a replayed turn outlives the view that typed it.
        send: (cid, text) => api.sendMessage(cid, text),
        outbox,
        schedule,
        openConversationId: conversationId,
        onRefused: (error) => setError(error),
      });
      syncQueued();
      // Read the open thread at the END, never at the start. The boot flush runs
      // BEFORE any companion is opened, so a start-of-flush snapshot is always
      // `null` there and the reload could never fire: the replayed turn left the
      // queue (so its "waiting to send" bubble vanished) and never appeared in the
      // history — delivered, paid for, and invisible until the next reload.
      const openId = conversationId();
      // A live run re-syncs on its own terminal event; reloading under it would
      // render the user's optimistic bubble twice.
      if (openId && delivered.has(openId) && !busy()) {
        await loadHistory(openId);
      }
    } finally {
      flushing = false;
    }
  };

  /**
   * Re-run the last user turn. The stream error line offers this as a real
   * control — a dead-end "Tap to retry" with nothing to tap is how a failed turn
   * becomes an unrecoverable conversation.
   */
  const retry = async (): Promise<void> => {
    const text = lastSentText;
    if (!text || busy()) {
      return;
    }
    await send(text);
  };

  const submit = async (directiveId: string, value: unknown): Promise<void> => {
    const convId = conversationId();
    const runId = stream().runId;
    if (!convId || !runId) {
      return;
    }
    const ack = await api.submitInput(convId, runId, value);
    if (isErr(ack)) {
      setError(ack.error);
      return;
    }
    // Optimistically un-park; the open SSE keeps tailing the resumed run.
    setStream((current) => markDirectiveAnswered(current, directiveId));
  };

  /**
   * Answer a parked `file` directive. The bytes go through the edge's concept
   * ingest (`POST /companions/:id/upload`), which re-validates type + size and
   * MODERATES the image — compliance red line 2, your own character concept only,
   * never a real identifiable person. Only the resulting ASSET reference is echoed
   * back to the run; raw bytes never touch the run channel.
   */
  const submitFile = async (directiveId: string, file: File): Promise<void> => {
    const gfId = companionId;
    if (!gfId || uploading()) {
      return;
    }
    setUploading(true);
    try {
      const ingested = await api.uploadConcept(gfId, file);
      if (isErr(ingested)) {
        // A rejected upload (moderation, size, type) must NOT un-park the run —
        // the user gets the reason and can pick a different image.
        setError(ingested.error);
        return;
      }
      await submit(directiveId, {
        girlfriendId: gfId,
        conceptAssetId: ingested.value.referenceAssetId,
      });
    } finally {
      setUploading(false);
    }
  };

  /**
   * Re-sync the open conversation from the authoritative history — driven by a WS
   * push (a proactive turn, or a turn sent on another device). A no-op when no
   * conversation is open or a run is mid-stream (the live tail is the fresher view;
   * the clean-finish path already re-syncs).
   */
  const refresh = async (): Promise<void> => {
    const convId = conversationId();
    if (!convId || busy()) {
      return;
    }
    await loadHistory(convId);
  };

  /** Apply a pushed title change only when it targets the open conversation. */
  const applyTitle = (conversationId: string, title: string): void => {
    setConversation((current) =>
      current && current.id === conversationId ? { ...current, title } : current,
    );
  };

  const dispose = (): void => {
    cancelStream();
    stopPolling();
  };

  return {
    conversation,
    conversationId,
    history,
    historyTruncated,
    stream,
    pendingUserText,
    busy,
    uploading,
    error,
    queued,
    flushOutbox,
    openCompanion,
    send,
    stop,
    submit,
    submitFile,
    retry,
    refresh,
    applyTitle,
    resolveMediaJob: (update) => media.resolveJob(update),
    reconcileMedia: () => media.reconcile(),
    dispose,
  };
}
